import './wrapper.scss';

import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import classNames from 'classnames';
import { Card, Button, Dropdown, Typography } from 'components/base';
import Icon, { ICON_TYPE } from 'components/icon';

const PageWrapper = props => {
  const dropdownMenuRef = useRef();

  const [isMenuVisible, setIsMenuVisible] = useState(false);
  const [isDesignSelectVisible, setIsDesignSelectVisible] = useState(false);

  const btnCircleClassName = classNames({
    'btn-circle': true,
    'btn-circle-left': props.buttonPosition === 'left',
  });

  let width;
  if (props.fullWidth) {
    width = 1;
  } else if (props.page.size.height > props.page.size.width) {
    width = props.page.size.width / props.page.size.height;
  } else {
    width = 1;
  }

  const handleOnToggleMenuClick = e => {
    e.preventDefault();

    setIsMenuVisible(value => !value);
  };

  const handleOnToggleDesignSelectClick = e => {
    e.preventDefault();

    setIsDesignSelectVisible(value => !value);
  };

  const handleOnChangePage = (e, id) => {
    e.preventDefault();

    props.onChangePage(id);
  };

  const handleOnClickOutside = e => {
    if (dropdownMenuRef.current) {
      if (dropdownMenuRef.current !== e.target && !dropdownMenuRef.current.contains(e.target)) {
        e.preventDefault();
        setIsMenuVisible(false);
        setIsDesignSelectVisible(false);
      }
    }
  };

  useEffect(() => {
    if (isMenuVisible || isDesignSelectVisible) {
      setTimeout(() => document.addEventListener('click', handleOnClickOutside));
    } else {
      document.removeEventListener('click', handleOnClickOutside);
    }

    return () => {
      document.removeEventListener('click', handleOnClickOutside);
    };
  }, [isMenuVisible, isDesignSelectVisible]);

  const showMenuToggler = props.onRemove || props.onDuplicate || props.onChangeOrientation;
  const renderMenu = () => {
    if (showMenuToggler) {
      if (isMenuVisible) {
        return (
          <>
            <Button
              size="xs"
              type="outline"
              className={btnCircleClassName}
              onClick={handleOnToggleMenuClick}
            >
              <Icon type={ICON_TYPE.REMOVE} />
            </Button>
            <Dropdown className="show dropdown-menu-compact">
              {props.onRemove && (
                <Dropdown.Item onClick={props.onRemove} className="has-icon">
                  <span>Delete</span>
                  <Icon type={ICON_TYPE.TRASH} />
                </Dropdown.Item>
              )}
              {props.onDuplicate && (
                <Dropdown.Item onClick={props.onDuplicate} className="has-icon">
                  <span>Duplicate</span>
                  <Icon type={ICON_TYPE.DUPLICATE} />
                </Dropdown.Item>
              )}
              {props.onChangeOrientation && (
                <Dropdown.Item onClick={props.onChangeOrientation} className="has-icon">
                  <span>Change orientation</span>
                  <Icon type={ICON_TYPE.ROTATE} />
                </Dropdown.Item>
              )}
            </Dropdown>
          </>
        );
      }

      return (
        <Button
          size="xs"
          type="outline"
          className={btnCircleClassName}
          onClick={handleOnToggleMenuClick}
        >
          <Icon type={ICON_TYPE.MORE} />
        </Button>
      );
    }

    return null;
  };

  const showDesignSelectToggler = props.otherPages.length > 1;
  const renderDesignSelect = () => {
    if (showDesignSelectToggler) {
      if (isDesignSelectVisible || props.showAlwaysOtherPages) {
        return (
          <>
            {!props.showAlwaysOtherPages && (
              <Button
                size="xs"
                type="outline"
                className={btnCircleClassName}
                onClick={handleOnToggleDesignSelectClick}
              >
                <Icon type={ICON_TYPE.REMOVE} />
              </Button>
            )}
            <div className={`editor-page-side-menu ${props.buttonPosition}`}>
              {props.otherPagesLabel && (
                <Typography.H6 className="mb-1">{props.otherPagesLabel}</Typography.H6>
              )}
              {props.otherPages.map(otherPage => {
                const active = props.page.id === otherPage.id;

                return (
                  <div
                    key={otherPage.id}
                    className={classNames({
                      'mb-1': true,
                      'border-active': active,
                      'border-transparent': !active,
                    })}
                    onClick={e => handleOnChangePage(e, otherPage.id)}
                  >
                    {otherPage.preview}
                  </div>
                );
              })}
            </div>
          </>
        );
      }

      return (
        <Button
          size="xs"
          type="outline"
          className={btnCircleClassName}
          onClick={handleOnToggleDesignSelectClick}
        >
          <Icon type={ICON_TYPE.MORE} />
        </Button>
      );
    }

    return null;
  };

  const offset = ((1 - width) * 100) / 2;

  const menuStyle = {
    position: 'absolute',
    width: `${100 - offset}%`,
    [props.buttonPosition]: `${offset}%`,
  };

  return (
    <Card
      className={classNames({
        'card-page mx-auto w-100': true,
        [props.className]: props.className,
      })}
    >
      {props.children}
      <div ref={dropdownMenuRef} style={menuStyle}>
        {renderMenu()}
        {renderDesignSelect()}
      </div>
    </Card>
  );
};

PageWrapper.propTypes = {
  page: PropTypes.object,
  buttonPosition: PropTypes.oneOf(['left', 'right']),
  otherPages: PropTypes.array,
  onChangePage: PropTypes.func,
  onRemove: PropTypes.func,
  onDuplicate: PropTypes.func,
  onChangeOrientation: PropTypes.func,
  fullWidth: PropTypes.bool,
  className: PropTypes.string,
  children: PropTypes.node,
  showAlwaysOtherPages: PropTypes.bool,
  otherPagesLabel: PropTypes.string,
};

PageWrapper.defaultProps = {
  className: 'mb-1',
  buttonPosition: 'right',
  otherPages: [],
  fullWidth: false,
  showAlwaysOtherPages: false,
};

export default PageWrapper;
